import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { BrandLockup } from "@/components/marketing/brand-lockup";
import { StartFreeButton } from "@/components/marketing/cta";
import { useMarketingSite } from "@/hooks/use-marketing-site";
import { LOGIN_PATH } from "@/lib/marketing/links";

/**
 * Public-site navigation (build F6): PadPulse for Hosts · Features ·
 * Pricing · Property managers · Free research · Log in · Start free.
 */
export const NAV_LINKS = [
  { label: "Features", href: "/features", testId: "features" },
  { label: "Pricing", href: "/pricing", testId: "pricing" },
  { label: "Property managers", href: "/property-managers", testId: "property-managers" },
  { label: "Free research", href: "/research", testId: "research" },
  { label: "Log in", href: LOGIN_PATH, testId: "login" },
];

export function PublicHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [location] = useLocation();
  useMarketingSite();

  const isActive = (href: string) => location === href || location.startsWith(`${href}/`);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  return (
    <header className="fixed top-0 z-50 w-full bg-[#0a1628]/95 backdrop-blur-xl" data-testid="header-nav">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 lg:h-20 items-center justify-between gap-4">
          <BrandLockup />

          <nav className="hidden lg:flex lg:items-center lg:gap-1" aria-label="Main">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={`px-3 xl:px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 min-h-[44px] flex items-center whitespace-nowrap ${
                  isActive(link.href) ? "text-brand-accent bg-white/10" : "text-white hover:text-white hover:bg-white/10"
                }`}
                data-testid={`link-nav-${link.testId}`}
              >
                {link.label}
              </Link>
            ))}
            <StartFreeButton ctaId="nav-start-free" location="header" size="default" showArrow={false} className="ml-2" />
          </nav>

          <div className="lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen((open) => !open)}
              className="text-white"
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              data-testid="button-mobile-menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </div>
        </div>
      </div>

      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          mobileMenuOpen ? "max-h-[85vh] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="bg-[#0a1628] border-t border-white/10 overflow-y-auto max-h-[85vh]">
          <nav className="flex flex-col px-4 py-3 space-y-1" aria-label="Mobile">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={`px-4 py-3.5 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
                  isActive(link.href) ? "text-brand-accent bg-white/10" : "text-white hover:text-white hover:bg-white/10"
                }`}
                data-testid={`link-mobile-${link.testId}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <StartFreeButton
              ctaId="mobile-start-free"
              location="header_mobile"
              size="default"
              showArrow={false}
              className="mt-2 w-full"
              onClick={() => setMobileMenuOpen(false)}
            />
          </nav>
        </div>
      </div>
    </header>
  );
}
